import { useMemo, useState } from "react"
import { Link, router } from "expo-router"
import Icon from "@react-native-vector-icons/material-design-icons"
import { ActivityIndicator, View, Pressable } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"

import { ar } from "i18n/ar"
import { formatDateForDisplay, localDateToUnix, unixToLocalDate, trpc, formatPrice, sharedStyles, dayjs } from "@src/utils"
import { List, Header, Text, ModalWrapper, DatePicker, MenuActions } from "@src/components"
import { ExpenseCategoryPicker } from "@src/components/expense-category-picker"

type Filters = { category?: number; startDate?: number; endDate?: number }

export default function Expenses() {
  const utils = trpc.useUtils()
  const filter$ = useObservable(false)
  const total$ = useObservable(false)
  const [filters, setFilters] = useState<Filters>(() => ({
    startDate: dayjs().subtract(7, "day").startOf("day").unix(),
    endDate: dayjs().endOf("day").unix(),
  }))

  const categories = trpc.expenseCategory.list.useQuery()
  const categoryMap = useMemo(() => {
    const m = new Map<number, { name: string; color: string | null }>()
    for (const c of categories.data?.items ?? []) m.set(c.id, { name: c.name, color: c.color })
    return m
  }, [categories.data])

  const expenses = trpc.expense.list.useInfiniteQuery(
    { category: filters.category, startDate: filters.startDate, endDate: filters.endDate },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  )
  const items = useMemo(() => expenses.data?.pages.flatMap((p) => p.items) ?? [], [expenses.data])
  const deleteExpense = trpc.expense.delete.useMutation()

  function hideFilters() {
    filter$.set(false)
  }

  function hideTotal() {
    total$.set(false)
  }

  async function handleDeleteExpense(id: number) {
    try {
      await deleteExpense.mutateAsync({ id })
      utils.expense.list.invalidate()
      return undefined
    } catch (error) {
      return error instanceof Error ? error.message : ar.somethingWentWrong
    }
  }

  const header = (
    <Header
      title={ar.expenses}
      right={
        <View className="flex-row h-full">
          <Pressable onPress={() => filter$.set(true)} className="p-2 h-full justify-center active:opacity-40">
            <Icon name="filter" size={24} color="#000" />
          </Pressable>

          <Pressable onPress={() => total$.set(true)} className="p-2 h-full justify-center active:opacity-40">
            <Icon name="sigma" size={24} color="#000" />
          </Pressable>

          <Link href="/dashboard/more/expense-categories" asChild>
            <Pressable className="p-2 h-full justify-center active:opacity-40">
              <Icon name="tag-multiple" size={24} color="#000" />
            </Pressable>
          </Link>

          <Pressable onPress={() => router.push("/dashboard/expenses/new")} className="p-2 h-full justify-center active:opacity-40">
            <Icon name="plus" size={24} color="#000" />
          </Pressable>
        </View>
      }
    />
  )

  if (expenses.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator color="#3B82F6" className="mt-4" />
      </>
    )
  }

  return (
    <>
      {header}

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={items}
        keyExtractor={(item) => item.id.toString()}
        onRefresh={expenses.refetch}
        onEndReached={() => {
          if (expenses.hasNextPage && !expenses.isFetchingNextPage) expenses.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={
          <View className="mt-4">
            <Text className="text-center text-gray-500">{ar.noExpenses}</Text>
          </View>
        }
        renderItem={({ item }) => {
          const cat = item.categoryId != null ? categoryMap.get(item.categoryId) : undefined
          const name = cat?.name ?? ar.uncategorized

          return (
            <View className="relative bg-white rounded border-gray-100 p-1.5 mb-1 shadow-sm flex-row justify-between items-start gap-2">
              <View className="flex-1">
                <View className="flex-row items-center gap-1 mb-1">
                  <View className="w-2.5 h-2.5 rounded-full bg-gray-400" style={cat?.color ? { backgroundColor: cat.color } : undefined} />
                  <Text className="text-base font-bold text-gray-700">{name}</Text>
                </View>

                {item.description && (
                  <Text className="text-sm text-gray-600 mb-1.5 leading-5" numberOfLines={2}>
                    {item.description}
                  </Text>
                )}

                <View className="flex-row items-center flex-wrap gap-1.5">
                  <View className="flex-row items-center gap-0.5">
                    <Icon name="clock-outline" size={13} color="#9CA3AF" />
                    <Text className="text-sm text-gray-400 mt-0.5">{formatDateForDisplay(item.createdAt, "D MMM • hh:mm a")}</Text>
                  </View>
                  <View className="flex-row items-center gap-1">
                    <Icon name="wallet" size={13} color="#9CA3AF" />
                    <Text className="text-sm text-gray-400 mt-0.5">{ar[item.balanceType]}</Text>
                  </View>
                </View>
              </View>

              <View className="items-end shrink-0">
                <MenuActions
                  className="relative z-20 top-1 end-1"
                  itemName={name}
                  onDelete={() => handleDeleteExpense(item.id)}
                  onEdit={() => router.push(`/dashboard/expenses/${item.id}/edit`)}
                />
                <Text className="text-base font-black text-red-600 mb-1 mt-3">
                  {formatPrice(Math.abs(item.amount))} <Text className="text-xs font-normal text-red-400">{ar.egp}</Text>
                </Text>
                <View className="items-end flex-row gap-2">
                  <Text className="text-xs text-gray-400">
                    {ar.balanceBefore}: {formatPrice(item.previousBalance)}
                  </Text>
                  <Text className="text-xs text-gray-400">
                    {ar.balanceAfter}: {formatPrice(item.newBalance)}
                  </Text>
                </View>
              </View>
            </View>
          )
        }}
        ListFooterComponent={
          <>
            {expenses.isFetchingNextPage && (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" color="#3B82F6" />
              </View>
            )}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />

      <Show if={filter$}>{() => <FiltersModal hide={hideFilters} setFilters={setFilters} filters={filters} />}</Show>
      <Show if={total$}>{() => <ExpensesTotal hide={hideTotal} filters={filters} />}</Show>
    </>
  )
}

type FiltersProps = {
  hide: () => void
  setFilters: (filters: Filters) => void
  filters: Filters
}

function FiltersModal({ hide, setFilters, filters }: FiltersProps) {
  const [category, setCategory] = useState<number | undefined>(filters.category)
  const [startDate, setStartDate] = useState<Date | undefined>(filters.startDate ? unixToLocalDate(filters.startDate) : undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(filters.endDate ? unixToLocalDate(filters.endDate) : undefined)

  function apply() {
    const start = startDate ? new Date(startDate.setHours(0, 0, 0, 0)) : undefined
    const end = endDate ? new Date(endDate.setHours(23, 59, 59, 999)) : undefined
    setFilters({
      category,
      startDate: start ? localDateToUnix(start) : undefined,
      endDate: end ? localDateToUnix(end) : undefined,
    })
    hide()
  }

  function clearFilters() {
    setStartDate(undefined)
    setEndDate(undefined)
    setCategory(undefined)
    setFilters({})
    hide()
  }

  return (
    <ModalWrapper onClose={hide} className="p-1.5">
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-xl font-bold">{ar.filters}</Text>
        <Pressable onPress={hide} hitSlop={20} className="bg-gray-100 p-2 rounded-full active:bg-gray-200">
          <Icon name="close" size={20} color="#374151" />
        </Pressable>
      </View>

      <View className="mb-4">
        <Text className="text-sm font-bold text-gray-900 mb-2">{ar.category}</Text>
        <ExpenseCategoryPicker id={category} onChange={setCategory} />
      </View>

      <View className="mb-5">
        <Text className="text-sm font-bold text-gray-900 mb-2">{ar.date}</Text>
        <View className="flex-row gap-3">
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1 font-medium">{ar.from}</Text>
            <View className="border border-gray-200 rounded overflow-hidden bg-white">
              <DatePicker value={startDate} onChange={setStartDate} mode="date" placeholder={ar.from} />
            </View>
          </View>
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1 font-medium">{ar.to}</Text>
            <View className="border border-gray-200 rounded overflow-hidden bg-white">
              <DatePicker value={endDate} onChange={setEndDate} mode="date" placeholder={ar.to} />
            </View>
          </View>
        </View>
      </View>

      <View className="flex-row gap-2.5">
        <Pressable onPress={clearFilters} className="flex-1 py-1.5 border border-gray-300 rounded active:bg-gray-50">
          <Text className="text-center text-gray-700 font-bold">{ar.clearFilters}</Text>
        </Pressable>

        <Pressable onPress={apply} className="flex-1 py-1.5 bg-blue-600 rounded active:opacity-90 shadow-sm">
          <Text className="text-center text-white font-bold">{ar.apply}</Text>
        </Pressable>
      </View>
    </ModalWrapper>
  )
}

type ExpensesTotalProps = {
  hide: () => void
  filters: Filters
}

function ExpensesTotal({ hide, filters }: ExpensesTotalProps) {
  // Fires on mount (component only renders while the modal is open via <Show>)
  const total = trpc.expense.total.useQuery({
    category: filters.category,
    startDate: filters.startDate,
    endDate: filters.endDate,
  })

  return (
    <ModalWrapper onClose={hide} className="p-1.5">
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-xl font-bold">{ar.totalExpenses}</Text>
        <Pressable onPress={hide} hitSlop={20} className="bg-gray-100 p-2 rounded-full active:bg-gray-200">
          <Icon name="close" size={20} color="#374151" />
        </Pressable>
      </View>

      {total.isLoading ? (
        <View className="items-center py-6">
          <ActivityIndicator color="#3B82F6" />
        </View>
      ) : (
        <View className="items-center py-2">
          <Text className="text-3xl font-black text-red-600 mb-1">
            {formatPrice(total.data?.total ?? 0)} <Text className="text-base font-normal text-red-400">{ar.egp}</Text>
          </Text>
          <Text className="text-sm text-gray-500">
            {ar.expensesCount}: {total.data?.count ?? 0}
          </Text>
        </View>
      )}
    </ModalWrapper>
  )
}
